"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import usersService from "../../services/usersService";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { InputCustom } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import AccountSettingsModal from "./AccountSettingsModal";

import {
  PencilSimple,
  EnvelopeSimple,
  ShieldCheck,
  CircleNotch,
  User as PhUser,
  Phone as PhPhone,
  Bell,
  BellRinging,
} from "phosphor-react";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const initialProfile = {
  name: "",
  email: "",
  phone: "",
  reference: "",
  avatarUrl: null,
  receiveSystemNotifications: true,
  receiveEmailNotifications: true,
};

export default function UserSettings() {
  const { checkAccess } = useAccessChecker();

  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);

  const [profile, setProfile] = useState(initialProfile);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [isEditing, setIsEditing] = useState(false);

  const [showAccountModal, setShowAccountModal] = useState(false);

  const canEdit = useMemo(() => checkAccess("settings", "update"), [checkAccess]);

  // Staff (any non-Client role) show a full name; clients show a reference.
  const isAdminOrSuper = useMemo(() => {
    return currentUser?.role?.name !== "Client";
  }, [currentUser]);

  const loadProfile = useCallback(async () => {
    try {
      setFetching(true);
      const res = await usersService.getProfile();
      const data = res?.data;

      if (data?.statusCode !== 200) {
        throw new Error(data?.message || "Failed to fetch profile data");
      }

      const userData = data?.data?.user;

      setCurrentUser(userData || null);

      const nextProfile = {
        name: userData?.fullName || "",
        email: userData?.email || "",
        phone: userData?.phone || "",
        reference: userData?.reference || "",
        avatarUrl: userData?.avatarUrl || null,
        receiveSystemNotifications:
          typeof userData?.receiveSystemNotifications === "boolean"
            ? userData.receiveSystemNotifications
            : true,
        receiveEmailNotifications:
          typeof userData?.receiveEmailNotifications === "boolean"
            ? userData.receiveEmailNotifications
            : true,
      };

      setProfile(nextProfile);
      setForm({
        name: nextProfile.name,
        email: nextProfile.email,
        phone: nextProfile.phone,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to fetch profile data");
      console.error("Profile fetch failed:", err?.response?.data || err);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const startEditing = useCallback(() => {
    setForm({ name: profile.name, email: profile.email, phone: profile.phone });
    setIsEditing(true);
  }, [profile]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setForm({ name: profile.name, email: profile.email, phone: profile.phone });
  }, [profile]);

  const validateForm = useCallback(() => {
    if (!form.name?.trim()) return "Name is required";
    if (!form.email?.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Invalid email format";
    return null;
  }, [form]);

  const toggleNotificationPref = useCallback(
    async (field, nextValue) => {
      const previous = profile[field];
      setProfile((p) => ({ ...p, [field]: nextValue }));
      try {
        const payload = {
          fullName: profile.name || "",
          phone: String(profile.phone || ""),
          [field]: nextValue,
        };
        const res = await usersService.updateProfile(payload);
        const data = res?.data;
        if (data?.statusCode !== 200) {
          throw new Error(data?.message || "Failed to update preference");
        }
        toast.success("Notification preference updated");
      } catch (err) {
        setProfile((p) => ({ ...p, [field]: previous }));
        toast.error(err?.response?.data?.message || err?.message || "Failed to update preference");
        console.error("Preference update failed:", err?.response?.data || err);
      }
    },
    [profile]
  );

  const saveChanges = useCallback(
    async (e) => {
      e.preventDefault();

      const error = validateForm();
      if (error) return toast.error(error);

      try {
        setSaving(true);

        const payload = {
          fullName: form.name.trim(),
          email: form.email.trim(),
          phone: String(form.phone || "").trim(),
        };

        const res = await usersService.updateProfile(payload);
        const data = res?.data;

        if (data?.statusCode !== 200) {
          throw new Error(data?.message || "Failed to update profile");
        }

        setProfile((p) => ({
          ...p,
          name: payload.fullName,
          email: payload.email,
          phone: payload.phone,
        }));

        setIsEditing(false);
        toast.success(data?.message || "Profile updated successfully");

        await loadProfile();
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to update profile");
        console.error("Profile update failed:", err?.response?.data || err);
      } finally {
        setSaving(false);
      }
    },
    [form, loadProfile, validateForm]
  );

  return (
    <div className="min-h-screen text-start bg-neutral-50 text-neutral-900 dark:bg-neutral-950 dark:text-white">

      {/* Fullscreen fetch overlay */}
      {fetching && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-neutral-950/80 px-4 py-3 shadow-xl">
            <CircleNotch className="h-5 w-5 animate-spin text-yellow-300" weight="bold" />
            <p className="text-sm text-neutral-100">Loading profile…</p>
          </div>
        </div>
      )}

      <div className="relative mx-auto  px-4 py-6 lg:px-8 lg:py-10">
     

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Profile card */}
          <div className="lg:col-span-2">
            <div className="relative overflow-hidden rounded-2xl border border-neutral-200/70 bg-white/80 p-5 shadow-sm backdrop-blur dark:border-neutral-900/60 dark:bg-neutral-900/20 lg:p-6">
              {/* top strip */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />

              {canEdit && !isEditing && (
                <button
                  onClick={startEditing}
                  className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50/90 px-3 py-1.5 text-[11px] font-[400]  text-neutral-700 transition hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
                >
                  <PencilSimple size={14} weight="bold" />
                  Edit profile
                </button>
              )}

              <div className="mb-6 flex items-center gap-4">
                <div className="h-12 w-12 overflow-hidden rounded-full bg-neutral-900 ring-2 ring-neutral-200/80 dark:ring-neutral-700/70">
                  {profile.avatarUrl ? (
                    <img src={profile.avatarUrl} alt="User" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <PhUser size={22} className="text-neutral-400" weight="bold" />
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="truncate text-sm font-[400] ">{profile.name || "Unnamed user"}</p>
                  <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                    Used for invoices, notifications and internal references.
                  </p>
                </div>
              </div>

              <div className="mb-6 h-px w-full bg-neutral-100 dark:bg-neutral-900/60" />

              {isEditing ? (
                <form onSubmit={saveChanges} className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <InputCustom
                      label="Name & surname"
                      placeholder="Enter name & surname"
                      type="text"
                      value={form.name}
                      onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                      icon={<PhUser size={18} className="text-neutral-500 dark:text-neutral-400" />}
                    />

                    <InputCustom
                      label="E-mail"
                      placeholder="Enter e-mail"
                      type="email"
                      value={form.email}
                      onChange={(value) => setForm((f) => ({ ...f, email: value }))}
                      icon={
                        <EnvelopeSimple
                          size={18}
                          className="text-neutral-500 dark:text-neutral-400"
                        />
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-[400]  text-neutral-700 dark:text-neutral-300">
                      Phone number
                    </label>

                    <div className="relative">
                      <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                        <PhPhone size={18} className="text-neutral-500 dark:text-neutral-400" />
                      </div>

                      <PhoneInput
                        country={"ma"}
                        value={form.phone}
                        onChange={(value) => setForm((f) => ({ ...f, phone: value }))}
                        inputClass="!w-full !h-11 !rounded-xl !border !border-neutral-200 dark:!border-neutral-800 !bg-white dark:!bg-neutral-900 !text-sm !text-neutral-900 dark:!text-white !pl-16 !pr-4 !focus:outline-none !focus:border-yellow-500 dark:!focus:border-yellow-400"
                        containerClass="!w-full"
                        buttonClass="!rounded-l-xl !border-neutral-200 dark:!border-neutral-800 !bg-neutral-100 dark:!bg-neutral-800"
                        dropdownClass="!bg-white dark:!bg-neutral-900 !text-black dark:!text-white"
                        searchClass="!bg-white dark:!bg-neutral-900 !text-black dark:!text-white"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className={cn(
                        "inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-xs font-[400] ",
                        "bg-yellow-500 text-neutral-900 hover:bg-yellow-400",
                        "focus:outline-none focus:ring-4 focus:ring-yellow-200 dark:focus:ring-yellow-500/20",
                        saving && "opacity-70"
                      )}
                    >
                      {saving ? (
                        <span className="inline-flex items-center gap-2">
                          <CircleNotch size={16} className="animate-spin" weight="bold" />
                          Saving…
                        </span>
                      ) : (
                        "Save changes"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={cancelEditing}
                      className={cn(
                        "inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-xs font-[400] ",
                        "bg-neutral-200 text-neutral-800 hover:bg-neutral-300",
                        "dark:bg-neutral-800 dark:text-white dark:hover:bg-neutral-700",
                        "focus:outline-none focus:ring-4 focus:ring-neutral-200 dark:focus:ring-neutral-700"
                      )}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-5">
                  {isAdminOrSuper ? (
                    <InfoRow label="Full name" value={profile.name || "-"} />
                  ) : (
                    <InfoRow label="Client reference" value={profile.reference || "-None-"} />
                  )}

                  <InfoRow label="E-mail" value={profile.email || "-"} />
                  <InfoRow label="Phone number" value={profile.phone || "Not set"} />
                </div>
              )}
            </div>
          </div>

          {/* Security card */}
          <div className="lg:col-span-1">
            <div className="relative overflow-hidden rounded-2xl border border-neutral-900/50 bg-neutral-950 p-5 shadow-sm dark:bg-neutral-900/20 lg:p-6">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />

              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-neutral-900/70 ring-1 ring-white/5">
                  <ShieldCheck size={18} className="text-yellow-300" weight="bold" />
                </div>

                <div className="min-w-0">
                  <h2 className="text-base font-[400]  text-neutral-50">Account & security</h2>
                  <p className="mt-1 text-sm text-neutral-400">
                    Manage email, password, 2FA, sessions and providers securely via Clerk.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowAccountModal(true)}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-yellow-500/60 bg-yellow-500/10 px-3 py-2.5 text-xs font-[400]  text-yellow-200 transition hover:bg-yellow-500/20"
              >
                <ShieldCheck size={16} weight="bold" />
                Open secure account settings
              </button>

              <p className="mt-4 text-xs leading-relaxed text-neutral-500">
                Changes here are handled by Clerk’s secure account center.
              </p>
            </div>

            {/* Notification preferences card */}
            <div className="relative mt-6 overflow-hidden rounded-2xl border border-neutral-200/70 bg-white/80 p-5 shadow-sm backdrop-blur dark:border-neutral-900/60 dark:bg-neutral-900/20 lg:p-6">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />

              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-2xl bg-neutral-100 ring-1 ring-neutral-200 dark:bg-neutral-900/70 dark:ring-white/5">
                  <Bell size={18} className="text-yellow-500 dark:text-yellow-300" weight="bold" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-[400] text-neutral-900 dark:text-neutral-50">Notifications</h2>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Choose how you want to be notified.
                  </p>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50/70 p-3 dark:border-neutral-800/60 dark:bg-neutral-900/40">
                  <div className="flex-1">
                    <p className="text-sm font-[400] text-neutral-900 dark:text-white flex items-center gap-2">
                      <Bell size={14} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                      System notifications
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                      Show in-app alerts inside the dashboard.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!profile.receiveSystemNotifications}
                      onChange={(e) =>
                        toggleNotificationPref("receiveSystemNotifications", e.target.checked)
                      }
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-neutral-200 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-yellow-500" />
                  </label>
                </div>

                <div className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50/70 p-3 dark:border-neutral-800/60 dark:bg-neutral-900/40">
                  <div className="flex-1">
                    <p className="text-sm font-[400] text-neutral-900 dark:text-white flex items-center gap-2">
                      <BellRinging size={14} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                      Email notifications
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                      Receive notification emails to {profile.email || "your inbox"}.
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!profile.receiveEmailNotifications}
                      onChange={(e) =>
                        toggleNotificationPref("receiveEmailNotifications", e.target.checked)
                      }
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-neutral-200 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-yellow-500" />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AccountSettingsModal show={showAccountModal} onClose={() => setShowAccountModal(false)} />
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <h3 className="mb-1.5 text-xs font-[400]  tracking-[0.14em] text-neutral-500 uppercase dark:text-neutral-400">
        {label}
      </h3>
      <p className="text-sm text-neutral-800 dark:text-neutral-200">{value}</p>
    </div>
  );
}